import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Trash2, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import ClienteForm from "../components/ClienteForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/clienti", { params: q ? { q } : {} });
    setClienti(data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const doDelete = async () => {
    try {
      await api.delete(`/clienti/${toDelete.id}`);
      toast.success("Cliente eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">Anagrafica e consensi · Area protetta separata dai dati ottici.</p>
        </div>
        <Button data-testid="new-cliente-btn" onClick={() => setOpenForm(true)} className="bg-[#1E40AF] hover:bg-[#1D4ED8] h-11">
          <Plus className="h-4 w-4 mr-2" /> Nuovo cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="clienti-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtra clienti…" className="pl-9 h-10 bg-white" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="clienti-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Cliente</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Contatti</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Consensi</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Stato</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clienti.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">Nessun cliente. Aggiungi il primo cliente.</td></tr>
            )}
            {clienti.map((c) => (
              <tr key={c.id} data-testid={`cliente-row-${c.id}`} onClick={() => navigate(`/clienti/${c.id}`)} className="hover:bg-slate-50 cursor-pointer">
                <td className="px-5 py-3.5">
                  <div className="font-medium text-slate-900">{c.cognome} {c.nome}</div>
                  <div className="text-xs text-slate-500">{c.data_nascita || "—"}</div>
                </td>
                <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">
                  <div>{c.telefono || "—"}</div>
                  <div className="text-xs text-slate-500">{c.email || "—"}</div>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex flex-wrap gap-1">
                    {c.consenso_marketing ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Marketing</Badge> : <Badge variant="outline" className="text-slate-400">No mkt</Badge>}
                    {c.consenso_whatsapp && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200">WhatsApp</Badge>}
                    {c.consenso_email && <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50 border border-sky-200">Email</Badge>}
                  </div>
                </td>
                <td className="px-5 py-3.5 hidden lg:table-cell">
                  {c.segmenti?.includes("attivi") ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Attivo</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><span className="h-2 w-2 rounded-full bg-slate-300" /> Inattivo</span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <button data-testid={`delete-cliente-${c.id}`} onClick={(e) => { e.stopPropagation(); setToDelete(c); }} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="flex items-center gap-2 text-xs text-slate-400">
        <ShieldCheck className="h-4 w-4" /> Privacy by Design: le prescrizioni ottiche sono archiviate separatamente e non compaiono in questa vista.
      </p>

      <ClienteForm open={openForm} onOpenChange={setOpenForm} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati anche i dati ottici collegati a {toDelete?.nome} {toDelete?.cognome}. Operazione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-cliente" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
